import React, { useEffect } from 'react';
import { css } from '@emotion/react';
import {
	Paper,
	Typography,
	Box,
	Button,
	FormControlLabel,
	Radio,
	RadioGroup,
	Stepper,
	Step,
	Link,
	StepLabel
} from '@mui/material';
import { column } from './commonCSS';
import { useMutationFetch, useQueryFetch } from './useQueryFetch';
import { useStore } from './store';
import TextField2FA from './TextField2FA';
import EmailCodeStep from './EmailCodeStep';
const { useQueryClient } = require('@cd/react-query');

//import {  } from '@mui/icons-material';
const stepContentStyle = css({
	marginLeft: '12px',
	paddingLeft: '20px',
	borderLeft: '1px solid #757575;'
});

const useFetch2FAQR = () => {
	const { data, isLoading } = useQueryFetch(
		'fetch 2fa qr',
		'/account/2fa/get/qr'
	);
	return { data, isLoading };
};

// Step 1 picks the method; email is the default. The authenticator QR (and
// its secret) is only generated once that method is chosen.
const Setup2FADlg = () => {
	const [method, setMethod] = React.useState('email');
	const [chosen, setChosen] = React.useState(false);

	return (
		<Paper css={column} sx={{ alignItems: 'center', p: 4 }}>
			{!chosen ? (
				<Box css={column} sx={{ maxWidth: 400 }}>
					<Typography variant="body2" sx={{ pb: 1 }}>
						How do you want to receive your verification codes?
					</Typography>
					<RadioGroup
						value={method}
						onChange={e => setMethod(e.target.value)}>
						<FormControlLabel
							value="email"
							control={<Radio />}
							label="Email - a code is sent to your account email"
						/>
						<FormControlLabel
							value="app"
							control={<Radio />}
							label="Authenticator app - Google Authenticator or similar"
						/>
					</RadioGroup>
					<Button
						sx={{ alignSelf: 'flex-end', mt: 2 }}
						variant="contained"
						onClick={() => setChosen(true)}>
						Continue
					</Button>
				</Box>
			) : method === 'email' ? (
				<EmailCodeStep
					purpose="setup"
					finishUrl="/account/2fa/email/finish"
					successMessage="2FA is setup!"
				/>
			) : (
				<AppSetup />
			)}
		</Paper>
	);
};

const AppSetup = () => {
	const { data } = useFetch2FAQR();
	return <VerticalLinearStepper qr={data?.qr} />;
};

const steps = [
	{
		label: 'Install an authentication app',
		description: (
			<span>
				Recommended: Download and configure for Android:{' '}
				<Link
					target="_blank"
					href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2">
					GOOGLE AUTHENTICATOR
				</Link>{' '}
				or{' '}
				<Link
					target="_blank"
					href="https://play.google.com/store/apps/details?id=org.shadowice.flocke.andotp">
					andOTP - OTP Authenticator
				</Link>
				. For iOS device:{' '}
				<Link
					target="_blank"
					href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458">
					MICROSOFT AUTHENTICATOR
				</Link>{' '}
			</span>
		)
	},
	{
		label: 'Add your account to the app',
		description:
			'Scan QR code below with your authentication app or enter the code manually.'
	},
	{
		label: 'Enter 6 digit verification code from the app',
		description: `Enter the 6 digit verification code generated by
              your authentication app.`
	}
];

function VerticalLinearStepper({ qr }) {
	const [activeStep, setActiveStep] = React.useState(0);
	const [value, setValue] = React.useState('');
	const [showIsInvalid, setShowIsInvalid] = React.useState(false);
	const queryClient = useQueryClient();
	const { dispatch } = useStore();
	const { mutate, data } = useMutationFetch('/account/2fa/setup/finish', {
		onSuccess: () => {
			queryClient.refetchQueries('fetch2fasettings');
		}
	});

	useEffect(() => {
		setActiveStep(value.length !== 0 ? 2 : 0);
	}, [value]);

	useEffect(() => {
		if (data?.isValid === false) {
			setShowIsInvalid(true);
		} else if (data?.isValid === true) {
			dispatch({ type: 'close dialog' });
			dispatch({ type: 'post success', message: '2FA is setup!' });
		}
	}, [data]);

	return (
		<Box sx={{ maxWidth: 400 }}>
			<Stepper
				activeStep={activeStep}
				orientation="vertical"
				sx={{ mt: 3 }}>
				{steps.map((step, index) => (
					<Step key={step.label}>
						<StepLabel>{step.label}</StepLabel>
						<Box sx={stepContentStyle}>
							<Typography variant="body2">
								{step.description}
							</Typography>
							{index === 1 && qr && (
								<img src={qr} alt="qr code" />
							)}
							{index === 2 && (
								<TextField2FA
									sx={{
										mt: 2,
										ml: 'auto',
										position: 'relative'
									}}
									value={value}
									setValue={setValue}
									showIsInvalid={showIsInvalid}
									setShowIsInvalid={setShowIsInvalid}
									onClick={e => {
										mutate({
											token: value
										});
									}}
								/>
							)}
						</Box>
					</Step>
				))}
			</Stepper>
		</Box>
	);
}
export default Setup2FADlg;
